import QRCode from "qrcode";
import { useEffect, useId, useRef, useState } from "react";
import type { Root } from "../../../../../supabase/functions/sync-control/model";
import { interfaceLocale, useTranslation } from "../../i18n";
import { QrCamera } from "./DeviceJoining";
import { pairQr, readPairQr } from "./pairing";
import { type PairCandidate, PairSession } from "./pairSession";
import { syncMessage } from "./syncMessage";
import type { useSyncReview } from "./useSyncReview";

export type PairingFeedbackState = "waiting" | "connecting" | "success" | "interrupted";
export function DevicePairing({
  controller,
  workspaceId,
  onMessage,
  onError: setError,
}: {
  controller: ReturnType<typeof useSyncReview>;
  workspaceId: string;
  onMessage: (message: string, state?: PairingFeedbackState) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation("syncReview");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [qr, setQr] = useState("");
  const [url, setUrl] = useState("");
  const [expires, setExpires] = useState(0);
  const [remember, setRemember] = useState(false);
  const [candidate, setCandidate] = useState<PairCandidate | null>(null);
  const [joining, setJoining] = useState<{ root: Root; material: string } | null>(null);
  const [localData, setLocalData] = useState(false);
  const [waitingApproval, setWaitingApproval] = useState(false);
  const [approved, setApproved] = useState(false);
  const stageTitleId = useId();
  const panel = useRef<HTMLElement>(null);
  const stageHeading = useRef<HTMLHeadingElement>(null);
  const initialStage = useRef(true);
  const session = useRef<PairSession | null>(null);
  const active = useRef(true);
  const polling = useRef(false);
  const authorized =
    !!controller.device.current &&
    controller.device.current.scope.datasetId === controller.status.summary?.root?.datasetId &&
    controller.device.current.scope.generation === controller.status.summary?.root?.generation;
  const perform = async (task: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await task();
    } catch (e) {
      setWaitingApproval(false);
      onMessage("");
      setError(t(syncMessage(e instanceof Error ? e.message : "")));
    } finally {
      if (active.current) setBusy(false);
    }
  };
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      session.current = null;
    };
  }, []);
  const scan = async (value: string) => {
    setApproved(false);
    onMessage(t("pairReading"), "waiting");
    const ticket = readPairQr(value, location.origin);
    setUrl("");
    const pair = PairSession.join(controller.api.current!, ticket);
    session.current = pair;
    setExpires(ticket.expiresAt);
    if (authorized) {
      const person = await pair.candidate();
      setCandidate(person);
      onMessage(t(person ? "checkPairDevice" : "pairReading"), person ? undefined : "waiting");
    } else {
      await pair.requestJoin();
      setWaitingApproval(true);
      onMessage(t("waitApproval"), "waiting");
    }
  };
  useEffect(() => {
    // Only the QR entry route carries a pairing ticket; preserve workspace anchors.
    if (!controller.api.current || location.pathname !== "/sync-pair" || !location.hash) return;
    const value = location.href;
    history.replaceState(null, "", `${location.pathname}${location.search}`);
    void perform(() => scan(value));
  }, [controller.status.ready]);
  useEffect(() => {
    const timer = setInterval(() => {
      const pair = session.current;
      if (!pair || busy || joining || polling.current) return;
      if (pair.ticket.expiresAt <= Date.now()) {
        session.current = null;
        setQr("");
        setCandidate(null);
        setWaitingApproval(false);
        onMessage("");
        setError(t("pairExpired"));
        return;
      }
      polling.current = true;
      void (async () => {
        try {
          if (authorized) {
            const person = await pair.candidate();
            if (active.current && session.current === pair && person) {
              setCandidate(person);
              setError("");
              onMessage(t("checkPairDevice"));
            }
          } else {
            const result = await pair.approval();
            if (!result || !active.current || session.current !== pair) return;
            const source = await controller.client!.exportBackupSource(workspaceId);
            if (!active.current) return;
            setLocalData(!!(source.snapshot.records.length || source.snapshot.syncArchives?.length));
            setJoining(result);
            setWaitingApproval(false);
            setQr("");
            setError("");
            onMessage(t("pairReady"));
          }
        } catch (e) {
          if (active.current && session.current === pair) {
            onMessage("");
            if (e instanceof Error && e.message === "sync-pair-expired") {
              session.current = null;
              setQr("");
              setCandidate(null);
              setWaitingApproval(false);
            }
            setError(
              t(
                e instanceof Error && e.message === "sync-pair-expired"
                  ? "pairExpired"
                  : syncMessage(e instanceof Error ? e.message : ""),
              ),
            );
          }
        } finally {
          polling.current = false;
        }
      })();
    }, 2000);
    return () => clearInterval(timer);
  }, [authorized, busy, joining, controller.client, workspaceId, t, onMessage, setError]);
  const create = () =>
    perform(async () => {
      onMessage("");
      const pair = await PairSession.create(controller.api.current!, authorized);
      session.current = pair;
      setCandidate(null);
      setJoining(null);
      setWaitingApproval(false);
      setApproved(false);
      setExpires(pair.ticket.expiresAt);
      setQr(
        await QRCode.toDataURL(pairQr(pair.ticket, location.origin), {
          errorCorrectionLevel: "M",
          scale: 6,
          margin: 4,
        }),
      );
      if (!authorized) await pair.requestJoin();
    });
  const resetView = () => {
    session.current = null;
    setQr("");
    setUrl("");
    setScanning(false);
    setCandidate(null);
    setWaitingApproval(false);
    setApproved(false);
    setError("");
    onMessage("");
  };
  const step = joining || approved ? 2 : candidate || waitingApproval ? 1 : 0;
  const choosing = !qr && !scanning && !candidate && !joining && !waitingApproval && !approved;
  const title = joining
    ? "pairDownloadTitle"
    : candidate
      ? "pairApproveTitle"
      : waitingApproval
        ? "pairWaitingTitle"
        : approved
          ? "pairApprovedTitle"
          : scanning
            ? "scanPairQr"
            : qr
              ? "pairShowTitle"
              : "pairOptionsTitle";
  const methods = authorized ? (["show", "scan"] as const) : (["scan", "show"] as const);
  useEffect(() => {
    if (initialStage.current) {
      initialStage.current = false;
      return;
    }
    stageHeading.current?.focus({ preventScroll: true });
    panel.current?.scrollIntoView({ block: "nearest" });
  }, [title]);
  return (
    <section ref={panel} className="sync-device-joining sync-pair-panel" aria-labelledby={stageTitleId}>
      <ol className="sync-pair-steps" aria-label={t("pairStepsLabel")}>
        {(["pairStepScan", "pairStepApprove", "pairStepDownload"] as const).map((key, index) => (
          <li
            key={key}
            className={index < step ? "is-complete" : index === step ? "is-current" : ""}
            aria-current={index === step ? "step" : undefined}
          >
            <b aria-hidden="true">{index < step ? "✓" : index + 1}</b>
            <span>{t(key)}</span>
          </li>
        ))}
      </ol>
      <h2 id={stageTitleId} ref={stageHeading} tabIndex={-1}>
        {t(title)}
      </h2>
      {choosing && (
        <>
          <p className="sync-pair-intro">{t("pairOptionsHint")}</p>
          <div className="sync-pair-methods">
            {methods.map((method, index) => (
              <button
                key={method}
                type="button"
                className={`sync-pair-method${index === 0 ? " is-primary" : ""}`}
                aria-label={t(method === "show" ? "showPairQr" : "scanPairQr")}
                disabled={busy || !controller.status.ready}
                onClick={() => (method === "show" ? void create() : setScanning(true))}
              >
                <PairingMethodIcon camera={method === "scan"} />
                <span>
                  <strong>{t(method === "show" ? "showPairQr" : "scanPairQr")}</strong>
                  <small>{t(method === "show" ? "pairShowHint" : "pairScanOptionHint")}</small>
                </span>
                <span className="sync-pair-method-arrow" aria-hidden="true">
                  ↗
                </span>
              </button>
            ))}
          </div>
        </>
      )}
      {qr && !candidate && !joining && (
        <div className="sync-pair-qr-card">
          <p className="sync-pair-intro">{t("pairScanHint")}</p>
          <img src={qr} width={320} height={320} alt={t("pair")} />
          <p className="sync-pair-expiry">
            {t("pairExpiry", { time: new Date(expires).toLocaleTimeString(interfaceLocale()) })}
          </p>
          <div className="sync-pair-secondary-actions">
            <button type="button" disabled={busy} onClick={() => void create()}>
              {t("pairRefreshQr")}
            </button>
            <button type="button" disabled={busy} onClick={resetView}>
              {t("pairBackToOptions")}
            </button>
          </div>
        </div>
      )}
      {scanning && (
        <QrCamera
          onResult={(value) => {
            setScanning(false);
            setQr("");
            void perform(() => scan(value));
          }}
          onClose={() => setScanning(false)}
        />
      )}
      {waitingApproval && !joining && (
        <div className="sync-pair-wait-card">
          <span className="sync-pair-wait-icon" aria-hidden="true">
            …
          </span>
          <p>{t("pairWaitingHint")}</p>
          <button type="button" onClick={resetView}>
            {t("pairBackToOptions")}
          </button>
        </div>
      )}
      {candidate && authorized && (
        <div className="sync-pair-approval-card">
          <p className="sync-pair-device-name">{t("approveDevice", { name: candidate.label })}</p>
          <p className="sync-pair-intro">{t("pairApproveHint")}</p>
          <button
            type="button"
            className="primary-action sync-pair-primary"
            disabled={busy}
            onClick={() =>
              void perform(async () => {
                await session.current!.approve(controller.device.current!);
                session.current = null;
                setCandidate(null);
                setQr("");
                setApproved(true);
                onMessage(t("pairApproved"));
              })
            }
          >
            {t("approvePairOnce")}
          </button>
        </div>
      )}
      {approved && (
        <div className="sync-pair-approved-card">
          <span className="sync-pair-done-icon" aria-hidden="true">
            ✓
          </span>
          <p>{t("pairApprovedHint")}</p>
          <button type="button" onClick={resetView}>
            {t("pairAnotherDevice")}
          </button>
        </div>
      )}
      {!joining && !candidate && !waitingApproval && !approved && !scanning && (
        <details className="sync-pair-link-entry">
          <summary>{t("pastePairUrl")}</summary>
          <label>
            {t("pairUrl")}
            <input type="password" autoComplete="off" value={url} onChange={(e) => setUrl(e.target.value)} />
          </label>
          <button type="button" disabled={busy || !url} onClick={() => void perform(() => scan(url))}>
            {t("readPairUrl")}
          </button>
        </details>
      )}
      {joining && (
        <div className="sync-pair-download-card">
          {localData ? (
            <p role="alert">{t("pairLocalDataRetained")}</p>
          ) : (
            <>
              <p className="sync-pair-intro">{t("pairDownloadHint")}</p>
              <label className="sync-review-check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={busy}
                />
                {t("privateDevice")}
              </label>
              <button
                type="button"
                className="primary-action sync-pair-primary"
                disabled={busy || !remember}
                onClick={() =>
                  void perform(async () => {
                    // The download can move this page into the classroom UI.
                    // Keep the durable pairing result separate from network success.
                    const pair = session.current;
                    onMessage(t("pairConnecting"), "connecting");
                    const failure = await controller.join(joining.root, joining.material, true);
                    // A retry resumes this pairing without replacing local data again.
                    session.current = null;
                    setJoining(null);
                    onMessage(t(failure ? "pairInterrupted" : "pairFinishing"), failure ? "interrupted" : "connecting");
                    await pair?.complete();
                    if (!failure) onMessage(t("pairConnected"), "success");
                  })
                }
              >
                {t("connectPairedDevice")}
              </button>
            </>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              session.current = null;
              setJoining(null);
              onMessage("");
            }}
          >
            {t("cancel")}
          </button>
        </div>
      )}
    </section>
  );
}

function PairingMethodIcon({ camera }: { camera: boolean }) {
  return (
    <svg
      className="sync-pair-method-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      {camera ? (
        <>
          <path
            d="M8 5.5 9.5 3h5L16 5.5h3a2 2 0 0 1 2 2V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5a2 2 0 0 1 2-2Z"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="13" r="4" />
        </>
      ) : (
        <>
          <rect x="3" y="3" width="6" height="6" rx="1" />
          <rect x="15" y="3" width="6" height="6" rx="1" />
          <rect x="3" y="15" width="6" height="6" rx="1" />
          <path d="M15 12v4h-3m9-4h-3v4h3v5h-6v-3h-3M3 12h6m3-9v6" />
        </>
      )}
    </svg>
  );
}
